import { useMediaDevices } from '@livekit/components-react';
import { Button, Menu, Text } from '@mantine/core';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { notifyDeviceListError } from '../media-errors';

type DeviceMenuProps = {
    kind: MediaDeviceKind;
    activeDeviceId: string | undefined;
    icon: ReactNode;
    label: string;
    onSelect: (deviceId: string) => void;
};

export function DeviceMenu({ kind, activeDeviceId, icon, label, onSelect }: Readonly<DeviceMenuProps>) {
    const { t } = useTranslation('prejoin');
    const devices = useMediaDevices({ kind, onError: notifyDeviceListError });
    const activeIndex = Math.max(
        devices.findIndex((device) => device.deviceId === activeDeviceId),
        0
    );
    const activeDevice = devices.at(activeIndex);
    const deviceLabel = (device: MediaDeviceInfo, index: number) => device.label || t('devices.unnamed', { index: index + 1 });

    return (
        <Menu position="bottom">
            <Menu.Target>
                <Button
                    aria-label={label}
                    disabled={devices.length === 0}
                    leftSection={icon}
                    radius="var(--radius-rounded-full)"
                    rightSection={<IconChevronDown size={20} />}
                    variant="default"
                    w="var(--spacing-56)"
                >
                    <Text inherit span truncate="end">
                        {activeDevice ? deviceLabel(activeDevice, activeIndex) : t('devices.none')}
                    </Text>
                </Button>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>{label}</Menu.Label>
                {devices.map((device, index) => (
                    <Menu.Item
                        key={device.deviceId}
                        rightSection={index === activeIndex ? <IconCheck size={16} /> : undefined}
                        onClick={() => {
                            onSelect(device.deviceId);
                        }}
                    >
                        {deviceLabel(device, index)}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
}
